import { useState, useEffect, useCallback, useMemo } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { BarChart3, Scissors, Shirt, ScanLine, Package, Download, FileText } from "lucide-react";

const typeLabels = {
  shirt: "Shirt", trouser: "Trouser/Pant", suit_jacket: "Suit Jacket",
  sherwani: "Sherwani", kurta: "Kurta", waistcoat: "Waistcoat",
  blazer: "Blazer", shalwar: "Shalwar", custom: "Custom"
};

const MONTHS = [
  { value: "01", label: "January" }, { value: "02", label: "February" },
  { value: "03", label: "March" }, { value: "04", label: "April" },
  { value: "05", label: "May" }, { value: "06", label: "June" },
  { value: "07", label: "July" }, { value: "08", label: "August" },
  { value: "09", label: "September" }, { value: "10", label: "October" },
  { value: "11", label: "November" }, { value: "12", label: "December" },
];

export default function AnalyticsPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(String(y));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/analytics/production", { params: { month: selectedMonth, year: selectedYear } });
      setData(res.data);
    } catch (err) { console.error("Failed to load analytics", err); }
    setLoading(false);
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label || "";

  // Pre-compute expensive sorts/max once per data change so the table body
  // doesn't re-sort on every render.
  const typeEntries = useMemo(
    () => Object.entries(data?.by_type || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );
  const workerEntries = useMemo(
    () => Object.entries(data?.by_worker || {}).sort((a, b) => b[1].total - a[1].total),
    [data]
  );
  const typeMaxCount = useMemo(
    () => Math.max(0, ...Object.values(data?.by_type || {})),
    [data]
  );
  const workerMaxCount = useMemo(
    () => Math.max(0, ...Object.values(data?.by_worker || {}).map(w => w.total)),
    [data]
  );

  if (loading) return (
    <div className="space-y-6">
      {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-card rounded-sm animate-pulse" />)}
    </div>
  );

  const topStats = [
    { label: `Scans (${monthLabel})`, value: data?.total_scans_this_month || 0, icon: ScanLine, color: "#D4AF37" },
    { label: `Articles Scanned`, value: data?.scanned_articles_this_month || 0, icon: Shirt, color: "#722F37" },
    { label: "All-Time Products", value: data?.total_products_all_time || 0, icon: Package, color: "#60a5fa" },
    { label: "All-Time Scans", value: data?.total_scans_all_time || 0, icon: ScanLine, color: "#4ade80" },
  ];

  const sortedWorkerType = [...(data?.by_worker_type || [])].sort((a, b) => b.count - a.count);

  return (
    <div data-testid="analytics-page" className="space-y-8">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">Production Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Scan QR codes to count articles made by each worker</p>
        </div>
      </div>

      {/* Month/Year Filter + Export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Month:</span>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="month-filter" className="w-40 bg-card border-border rounded-sm h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger data-testid="year-filter" className="w-28 bg-card border-border rounded-sm h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${BACKEND_URL}/api/analytics/export/excel?month=${selectedMonth}&year=${selectedYear}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("vr_token");
              fetch(`${BACKEND_URL}/api/analytics/export/excel?month=${selectedMonth}&year=${selectedYear}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).then(res => res.blob()).then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `VR_Darzi_Production_${monthLabel}_${selectedYear}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              });
            }}
          >
            <Button data-testid="export-excel-btn" variant="outline" className="border-border rounded-sm gap-2 h-9 text-sm">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </a>
          <a
            href={`${BACKEND_URL}/api/analytics/export/pdf?month=${selectedMonth}&year=${selectedYear}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("vr_token");
              fetch(`${BACKEND_URL}/api/analytics/export/pdf?month=${selectedMonth}&year=${selectedYear}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).then(res => res.blob()).then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `VR_Darzi_Production_${monthLabel}_${selectedYear}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              });
            }}
          >
            <Button data-testid="export-pdf-btn" variant="outline" className="border-border rounded-sm gap-2 h-9 text-sm">
              <FileText className="w-3.5 h-3.5" /> PDF
            </Button>
          </a>
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        {topStats.map((stat) => (
          <Card key={stat.label} className="luxury-card rounded-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground font-serif">{stat.value}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(data?.scanned_articles_this_month || 0) === 0 ? (
        <Card className="luxury-card rounded-sm">
          <CardContent className="p-12 text-center">
            <ScanLine className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
            <p className="text-muted-foreground text-sm">No scans recorded for {monthLabel} {selectedYear}</p>
            <p className="text-muted-foreground/60 text-xs mt-1">Scan slip QR codes to start tracking production</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Articles by Type (from scans) */}
            <Card className="luxury-card rounded-sm">
              <CardHeader>
                <CardTitle className="font-serif text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 gold-text" /> Articles by Type
                </CardTitle>
                <p className="text-xs text-muted-foreground">{monthLabel} {selectedYear} - from QR scans</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {typeEntries.map(([type, count]) => {
                    const pct = typeMaxCount > 0 ? (count / typeMaxCount) * 100 : 0;
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-foreground capitalize">{typeLabels[type] || type}</span>
                          <span className="text-foreground font-semibold font-serif">{count}</span>
                        </div>
                        <div className="h-2 bg-background rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "#722F37" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Articles by Worker (from scans) */}
            <Card className="luxury-card rounded-sm">
              <CardHeader>
                <CardTitle className="font-serif text-base flex items-center gap-2">
                  <Scissors className="w-4 h-4 gold-text" /> Articles by Worker
                </CardTitle>
                <p className="text-xs text-muted-foreground">{monthLabel} {selectedYear} - from QR scans</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workerEntries.map(([name, info]) => {
                    const pct = workerMaxCount > 0 ? (info.total / workerMaxCount) * 100 : 0;
                    return (
                      <div key={name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-foreground">{name}</span>
                          <span className="text-foreground font-semibold font-serif">{info.total}</span>
                        </div>
                        <div className="h-2 bg-background rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "#D4AF37" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed: Worker x Article Type */}
          <Card className="luxury-card rounded-sm">
            <CardHeader>
              <CardTitle className="font-serif text-base">
                Worker Production Detail - {monthLabel} {selectedYear}
              </CardTitle>
              <p className="text-xs text-muted-foreground">How many of each article type each worker produced (from QR scans)</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="luxury-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Worker</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Article Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedWorkerType.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No scan data for this month</TableCell>
                    </TableRow>
                  ) : sortedWorkerType.map((item, idx) => (
                    <TableRow key={`${item.worker_name}-${item.clothing_type}-${idx}`} className="border-border">
                      <TableCell className="text-sm text-foreground font-medium">{item.worker_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] rounded-sm capitalize">{typeLabels[item.clothing_type] || item.clothing_type}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold font-serif gold-text">{item.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Info */}
      <div className="bg-[rgba(212,175,55,0.05)] border border-[rgba(212,175,55,0.12)] rounded-sm p-4">
        <p className="text-xs text-muted-foreground">
          <span className="gold-text font-medium">How it works:</span> Each slip has a QR code. When you scan it, the system logs which worker made which articles. At the end of the month, select the month above to see the total count of articles per worker. Use this to calculate worker output and payments.
        </p>
      </div>
    </div>
  );
}
